import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, ContactDetail as ContactDetailType, CreateInteraction, CreateTask, CreateContact, Interaction, Task } from '../api/client';

const statusColors: Record<string, string> = {
  lead: 'bg-blue-100 text-blue-800',
  contactado: 'bg-yellow-100 text-yellow-800',
  en_negociacion: 'bg-orange-100 text-orange-800',
  cliente: 'bg-green-100 text-green-800',
  perdido: 'bg-red-100 text-red-800',
};

const interactionTypeLabels: Record<string, string> = {
  email_enviado: '📧 Email Enviado',
  dm_enviado: '💬 DM Enviado',
  llamada: '📞 Llamada',
  reunion: '🤝 Reunión',
  nota: '📝 Nota',
};

export default function ContactDetail() {
  const { id } = useParams<{ id: string }>();
  const [contact, setContact] = useState<ContactDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submittingInteraction, setSubmittingInteraction] = useState(false);
  const [submittingTask, setSubmittingTask] = useState(false);
  const [submittingEdit, setSubmittingEdit] = useState(false);
  const [submittingService, setSubmittingService] = useState(false);

  // Modo edición
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<CreateContact>>({});

  // Edición de servicios
  const [editingServiceId, setEditingServiceId] = useState<number | null>(null);
  const [serviceEditForm, setServiceEditForm] = useState({
    status: '',
    monthlyFee: '',
    notes: '',
  });

  // Alta de servicios
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [serviceForm, setServiceForm] = useState({
    serviceType: 'optimizacion_listing',
    monthlyFee: '',
    startDate: '',
    notes: '',
  });
  const [submittingNewService, setSubmittingNewService] = useState(false);

  // Baja de servicios
  const [deletingServiceId, setDeletingServiceId] = useState<number | null>(null);

  // Formulario de interacción
  const [showInteractionForm, setShowInteractionForm] = useState(false);
  const [interactionForm, setInteractionForm] = useState<CreateInteraction>({
    type: 'nota',
    summary: '',
  });

  // Edición de interacciones
  const [editingInteractionId, setEditingInteractionId] = useState<number | null>(null);
  const [interactionEditForm, setInteractionEditForm] = useState<CreateInteraction>({
    type: 'nota',
    summary: '',
  });
  const [submittingInteractionEdit, setSubmittingInteractionEdit] = useState(false);
  const [deletingInteractionId, setDeletingInteractionId] = useState<number | null>(null);

  // Formulario de tarea
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [taskForm, setTaskForm] = useState<CreateTask>({
    title: '',
    dueDate: '',
  });

  // Edición de tareas
  const [editingTaskId, setEditingTaskId] = useState<number | null>(null);
  const [taskEditForm, setTaskEditForm] = useState<{ title: string; dueDate: string }>({
    title: '',
    dueDate: '',
  });
  const [submittingTaskEdit, setSubmittingTaskEdit] = useState(false);
  const [deletingTaskId, setDeletingTaskId] = useState<number | null>(null);

  useEffect(() => {
    if (id) loadContact(parseInt(id));
  }, [id]);

  const loadContact = async (contactId: number) => {
    try {
      const response = await api.contacts.get(contactId);
      setContact(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar contacto');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!contact) return;
    try {
      await api.contacts.update(contact.id, { status: newStatus as any });
      setContact({ ...contact, status: newStatus });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error al actualizar');
    }
  };

  // Iniciar modo edición
  const startEditing = () => {
    if (!contact) return;
    setEditForm({
      name: contact.name || '',
      company: contact.company || '',
      email: contact.email || '',
      phone: contact.phone || '',
      amazonStoreUrl: contact.amazon_store_url || '',
      channel: contact.channel as any || 'email',
      sourceNotes: contact.source_notes || '',
    });
    setIsEditing(true);
  };

  // Cancelar edición
  const cancelEditing = () => {
    setIsEditing(false);
    setEditForm({});
  };

  // Guardar cambios de edición
  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contact || submittingEdit) return;
    setSubmittingEdit(true);

    try {
      await api.contacts.update(contact.id, editForm);
      await loadContact(contact.id);
      setIsEditing(false);
      setEditForm({});
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error al guardar cambios');
    } finally {
      setSubmittingEdit(false);
    }
  };

  // Iniciar edición de servicio
  const startEditingService = (service: any) => {
    setEditingServiceId(service.id);
    setServiceEditForm({
      status: service.status || 'activo',
      monthlyFee: service.monthly_fee?.toString() || '',
      notes: service.notes || '',
    });
  };

  // Cancelar edición de servicio
  const cancelEditingService = () => {
    setEditingServiceId(null);
    setServiceEditForm({ status: '', monthlyFee: '', notes: '' });
  };

  // Guardar cambios de servicio
  const handleSaveService = async (serviceId: number) => {
    if (!contact || submittingService) return;
    setSubmittingService(true);

    try {
      await api.services.update(contact.id, serviceId, {
        status: serviceEditForm.status,
        monthlyFee: serviceEditForm.monthlyFee ? parseFloat(serviceEditForm.monthlyFee) : undefined,
        notes: serviceEditForm.notes || undefined,
      });
      await loadContact(contact.id);
      setEditingServiceId(null);
      setServiceEditForm({ status: '', monthlyFee: '', notes: '' });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error al actualizar servicio');
    } finally {
      setSubmittingService(false);
    }
  };

  // ---- Servicios: agregar / eliminar ----
  const handleAddService = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contact || submittingNewService) return;
    setSubmittingNewService(true);

    try {
      await api.services.create(contact.id, {
        serviceType: serviceForm.serviceType,
        monthlyFee: serviceForm.monthlyFee ? parseFloat(serviceForm.monthlyFee) : undefined,
        startDate: serviceForm.startDate || undefined,
        notes: serviceForm.notes || undefined,
      });
      setShowServiceForm(false);
      setServiceForm({ serviceType: 'optimizacion_listing', monthlyFee: '', startDate: '', notes: '' });
      await loadContact(contact.id);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error al agregar servicio');
    } finally {
      setSubmittingNewService(false);
    }
  };

  const handleDeleteService = async (serviceId: number) => {
    if (!contact || deletingServiceId) return;
    if (!confirm('¿Eliminar este servicio? Esta acción no se puede deshacer.')) return;
    setDeletingServiceId(serviceId);

    try {
      await api.services.delete(contact.id, serviceId);
      if (editingServiceId === serviceId) cancelEditingService();
      await loadContact(contact.id);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error al eliminar servicio');
    } finally {
      setDeletingServiceId(null);
    }
  };

  const handleAddInteraction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contact || submittingInteraction) return;
    setSubmittingInteraction(true);

    try {
      await api.interactions.create(contact.id, interactionForm);
      setShowInteractionForm(false);
      setInteractionForm({ type: 'nota', summary: '' });
      loadContact(contact.id);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error al agregar interacción');
    } finally {
      setSubmittingInteraction(false);
    }
  };

  const handleAddTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contact || submittingTask) return;
    setSubmittingTask(true);

    try {
      await api.tasks.create({
        contactId: contact.id,
        title: taskForm.title,
        dueDate: taskForm.dueDate || undefined,
      });
      setShowTaskForm(false);
      setTaskForm({ title: '', dueDate: '' });
      loadContact(contact.id);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error al agregar tarea');
    } finally {
      setSubmittingTask(false);
    }
  };

  // ---- Interacciones: editar / eliminar ----
  const startEditingInteraction = (interaction: Interaction) => {
    setEditingInteractionId(interaction.id);
    setInteractionEditForm({
      type: interaction.type as CreateInteraction['type'],
      summary: interaction.summary || '',
      nextAction: interaction.next_action || '',
      nextActionDate: interaction.next_action_date || '',
    });
  };

  const cancelEditingInteraction = () => {
    setEditingInteractionId(null);
    setInteractionEditForm({ type: 'nota', summary: '' });
  };

  const handleSaveInteraction = async (interactionId: number) => {
    if (!contact || submittingInteractionEdit) return;
    if (!interactionEditForm.summary.trim()) {
      alert('El resumen es obligatorio');
      return;
    }
    setSubmittingInteractionEdit(true);

    try {
      await api.interactions.update(contact.id, interactionId, {
        type: interactionEditForm.type,
        summary: interactionEditForm.summary,
        nextAction: interactionEditForm.nextAction || undefined,
        nextActionDate: interactionEditForm.nextActionDate || undefined,
      });
      setEditingInteractionId(null);
      setInteractionEditForm({ type: 'nota', summary: '' });
      await loadContact(contact.id);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error al actualizar interacción');
    } finally {
      setSubmittingInteractionEdit(false);
    }
  };

  const handleDeleteInteraction = async (interactionId: number) => {
    if (!contact || deletingInteractionId) return;
    if (!confirm('¿Eliminar esta interacción? Esta acción no se puede deshacer.')) return;
    setDeletingInteractionId(interactionId);

    try {
      await api.interactions.delete(contact.id, interactionId);
      if (editingInteractionId === interactionId) cancelEditingInteraction();
      await loadContact(contact.id);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error al eliminar interacción');
    } finally {
      setDeletingInteractionId(null);
    }
  };

  // ---- Tareas: editar / eliminar ----
  const startEditingTask = (task: Task) => {
    setEditingTaskId(task.id);
    setTaskEditForm({
      title: task.title,
      dueDate: task.due_date || '',
    });
  };

  const cancelEditingTask = () => {
    setEditingTaskId(null);
    setTaskEditForm({ title: '', dueDate: '' });
  };

  const handleSaveTask = async (taskId: number) => {
    if (!contact || submittingTaskEdit) return;
    if (!taskEditForm.title.trim()) {
      alert('El título es obligatorio');
      return;
    }
    setSubmittingTaskEdit(true);

    try {
      await api.tasks.update(taskId, {
        title: taskEditForm.title,
        dueDate: taskEditForm.dueDate || null,
      });
      setEditingTaskId(null);
      setTaskEditForm({ title: '', dueDate: '' });
      await loadContact(contact.id);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error al actualizar tarea');
    } finally {
      setSubmittingTaskEdit(false);
    }
  };

  const handleDeleteTask = async (taskId: number) => {
    if (!contact || deletingTaskId) return;
    if (!confirm('¿Eliminar esta tarea? Esta acción no se puede deshacer.')) return;
    setDeletingTaskId(taskId);

    try {
      await api.tasks.delete(taskId);
      if (editingTaskId === taskId) cancelEditingTask();
      await loadContact(contact.id);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error al eliminar tarea');
    } finally {
      setDeletingTaskId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amazon-orange"></div>
      </div>
    );
  }

  if (error || !contact) {
    return (
      <div className="bg-red-50 text-red-600 p-4 rounded-lg">
        {error || 'Contacto no encontrado'}
        <Link to="/contacts" className="ml-4 text-amazon-orange hover:underline">
          Volver a contactos
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/contacts" className="text-gray-400 hover:text-gray-600 shrink-0">
          ← Volver
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-11 h-11 sm:w-16 sm:h-16 shrink-0 bg-amazon-orange rounded-full flex items-center justify-center text-white text-lg sm:text-2xl font-bold">
              {contact.name.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl font-bold text-gray-800 break-words">{contact.name}</h1>
              {contact.company && (
                <p className="text-sm text-gray-500 truncate">{contact.company}</p>
              )}
            </div>
          </div>
        </div>
        <select
          value={contact.status}
          onChange={(e) => handleStatusChange(e.target.value)}
          className={`shrink-0 px-3 sm:px-4 py-2 rounded-lg font-medium text-sm ${statusColors[contact.status]}`}
        >
          <option value="lead">Lead</option>
          <option value="contactado">Contactado</option>
          <option value="en_negociacion">En Negociación</option>
          <option value="cliente">Cliente</option>
          <option value="perdido">Perdido</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        {/* Información del contacto */}
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">📋 Información</h2>
            {!isEditing && (
              <button
                onClick={startEditing}
                className="text-amazon-orange hover:underline text-sm"
              >
                ✏️ Editar
              </button>
            )}
          </div>

          {isEditing ? (
            <form onSubmit={handleSaveEdit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                <input
                  type="text"
                  value={editForm.name || ''}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amazon-orange focus:border-transparent outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Empresa</label>
                <input
                  type="text"
                  value={editForm.company || ''}
                  onChange={(e) => setEditForm({ ...editForm, company: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amazon-orange focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={editForm.email || ''}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amazon-orange focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                <input
                  type="tel"
                  value={editForm.phone || ''}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amazon-orange focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL Tienda Amazon</label>
                <input
                  type="url"
                  value={editForm.amazonStoreUrl || ''}
                  onChange={(e) => setEditForm({ ...editForm, amazonStoreUrl: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amazon-orange focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Canal</label>
                <select
                  value={editForm.channel || 'email'}
                  onChange={(e) => setEditForm({ ...editForm, channel: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amazon-orange focus:border-transparent outline-none"
                >
                  <option value="email">📧 Email</option>
                  <option value="dm">💬 DM</option>
                  <option value="llamada">📞 Llamada</option>
                  <option value="referido">👥 Referido</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
                <textarea
                  value={editForm.sourceNotes || ''}
                  onChange={(e) => setEditForm({ ...editForm, sourceNotes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amazon-orange focus:border-transparent outline-none"
                  rows={3}
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={submittingEdit}
                  className="bg-amazon-orange text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {submittingEdit ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                      Guardando...
                    </>
                  ) : (
                    '💾 Guardar Cambios'
                  )}
                </button>
                <button
                  type="button"
                  onClick={cancelEditing}
                  className="text-gray-500 px-4 py-2 text-sm"
                >
                  Cancelar
                </button>
              </div>
            </form>
          ) : (
            <dl className="space-y-3 max-h-80 overflow-y-auto pr-2 -mr-2">
              <div>
                <dt className="text-sm text-gray-500">Email</dt>
                <dd className="text-gray-800">{contact.email || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Teléfono</dt>
                <dd className="text-gray-800">{contact.phone || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Canal</dt>
                <dd className="text-gray-800">{contact.channel || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Tienda Amazon</dt>
                <dd className="text-gray-800">
                  {contact.amazon_store_url ? (
                    <a href={contact.amazon_store_url} target="_blank" rel="noopener noreferrer" className="text-amazon-orange hover:underline">
                      {contact.amazon_store_url}
                    </a>
                  ) : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Notas</dt>
                <dd className="text-gray-800">{contact.source_notes || '-'}</dd>
              </div>
            </dl>
          )}
        </div>

        {/* Timeline de interacciones */}
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">💬 Interacciones</h2>
            <button
              onClick={() => setShowInteractionForm(true)}
              className="text-amazon-orange hover:underline text-sm"
            >
              + Agregar
            </button>
          </div>

          {showInteractionForm && (
            <form onSubmit={handleAddInteraction} className="mb-4 p-4 bg-gray-50 rounded-lg space-y-3">
              <select
                value={interactionForm.type}
                onChange={(e) => setInteractionForm({ ...interactionForm, type: e.target.value as any })}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="email_enviado">Email Enviado</option>
                <option value="dm_enviado">DM Enviado</option>
                <option value="llamada">Llamada</option>
                <option value="reunion">Reunión</option>
                <option value="nota">Nota</option>
              </select>
              <textarea
                value={interactionForm.summary}
                onChange={(e) => setInteractionForm({ ...interactionForm, summary: e.target.value })}
                placeholder="Resumen de la interacción..."
                className="w-full px-3 py-2 border rounded-lg"
                rows={3}
                required
              />
              <input
                type="text"
                value={interactionForm.nextAction || ''}
                onChange={(e) => setInteractionForm({ ...interactionForm, nextAction: e.target.value })}
                placeholder="Próxima acción (opcional)"
                className="w-full px-3 py-2 border rounded-lg"
              />
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={submittingInteraction}
                  className="bg-amazon-orange text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {submittingInteraction ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                      Guardando...
                    </>
                  ) : (
                    'Guardar'
                  )}
                </button>
                <button type="button" onClick={() => setShowInteractionForm(false)} className="text-gray-500 text-sm">
                  Cancelar
                </button>
              </div>
            </form>
          )}

          <div className="space-y-3 max-h-80 overflow-y-auto pr-2 -mr-2">
            {contact.interactions.length === 0 ? (
              <p className="text-gray-500 text-sm">No hay interacciones registradas</p>
            ) : (
              contact.interactions.map((interaction) => (
                <div key={interaction.id} className="border-l-4 border-amazon-orange pl-4 py-2">
                  {editingInteractionId === interaction.id ? (
                    // Formulario de edición de interacción
                    <div className="space-y-2 bg-gray-50 p-3 rounded-lg border border-gray-200">
                      <select
                        value={interactionEditForm.type}
                        onChange={(e) => setInteractionEditForm({ ...interactionEditForm, type: e.target.value as any })}
                        className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-amazon-orange outline-none"
                      >
                        <option value="email_enviado">Email Enviado</option>
                        <option value="dm_enviado">DM Enviado</option>
                        <option value="llamada">Llamada</option>
                        <option value="reunion">Reunión</option>
                        <option value="nota">Nota</option>
                      </select>
                      <textarea
                        value={interactionEditForm.summary}
                        onChange={(e) => setInteractionEditForm({ ...interactionEditForm, summary: e.target.value })}
                        placeholder="Resumen de la interacción..."
                        className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-amazon-orange outline-none"
                        rows={3}
                        required
                      />
                      <input
                        type="text"
                        value={interactionEditForm.nextAction || ''}
                        onChange={(e) => setInteractionEditForm({ ...interactionEditForm, nextAction: e.target.value })}
                        placeholder="Próxima acción (opcional)"
                        className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-amazon-orange outline-none"
                      />
                      <input
                        type="date"
                        value={interactionEditForm.nextActionDate || ''}
                        onChange={(e) => setInteractionEditForm({ ...interactionEditForm, nextActionDate: e.target.value })}
                        className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-amazon-orange outline-none"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSaveInteraction(interaction.id)}
                          disabled={submittingInteractionEdit}
                          className="bg-amazon-orange text-white px-3 py-1 rounded text-xs disabled:opacity-50 flex items-center gap-1"
                        >
                          {submittingInteractionEdit ? (
                            <>
                              <div className="animate-spin rounded-full h-2 w-2 border-b-2 border-white"></div>
                              Guardando...
                            </>
                          ) : (
                            '💾 Guardar'
                          )}
                        </button>
                        <button
                          onClick={cancelEditingInteraction}
                          disabled={submittingInteractionEdit}
                          className="text-gray-500 px-3 py-1 text-xs"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Vista normal de la interacción
                    <>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium">
                          {interactionTypeLabels[interaction.type]}
                        </span>
                        <span className="text-xs text-gray-400 flex-1">
                          {new Date(interaction.created_at).toLocaleDateString('es')}
                        </span>
                        <button
                          onClick={() => startEditingInteraction(interaction)}
                          title="Editar interacción"
                          className="p-1.5 sm:p-0 text-gray-400 hover:text-amazon-orange text-sm sm:text-xs"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteInteraction(interaction.id)}
                          disabled={deletingInteractionId === interaction.id}
                          title="Eliminar interacción"
                          className="p-1.5 sm:p-0 text-gray-400 hover:text-red-600 text-sm sm:text-xs disabled:opacity-50"
                        >
                          {deletingInteractionId === interaction.id ? '⏳' : '🗑️'}
                        </button>
                      </div>
                      <p className="text-sm text-gray-600">{interaction.summary}</p>
                      {interaction.next_action && (
                        <p className="text-xs text-amazon-orange mt-1">
                          → {interaction.next_action}
                          {interaction.next_action_date && (
                            <span className="text-gray-400"> ({interaction.next_action_date})</span>
                          )}
                        </p>
                      )}
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Tareas y servicios */}
        <div className="space-y-6">
          {/* Tareas */}
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">✅ Tareas</h2>
              <button
                onClick={() => setShowTaskForm(true)}
                className="text-amazon-orange hover:underline text-sm"
              >
                + Agregar
              </button>
            </div>

            {showTaskForm && (
              <form onSubmit={handleAddTask} className="mb-4 p-4 bg-gray-50 rounded-lg space-y-3">
                <input
                  type="text"
                  value={taskForm.title}
                  onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                  placeholder="Título de la tarea"
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
                <input
                  type="date"
                  value={taskForm.dueDate || ''}
                  onChange={(e) => setTaskForm({ ...taskForm, dueDate: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={submittingTask}
                    className="bg-amazon-orange text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {submittingTask ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                        Guardando...
                      </>
                    ) : (
                      'Guardar'
                    )}
                  </button>
                  <button type="button" onClick={() => setShowTaskForm(false)} className="text-gray-500 text-sm">
                    Cancelar
                  </button>
                </div>
              </form>
            )}

            <div className="space-y-2 max-h-80 overflow-y-auto pr-2 -mr-2">
              {contact.pendingTasks.length === 0 ? (
                <p className="text-gray-500 text-sm">No hay tareas pendientes</p>
              ) : (
                contact.pendingTasks.map((task) => (
                  <div key={task.id} className="p-2 bg-gray-50 rounded">
                    {editingTaskId === task.id ? (
                      // Formulario de edición de tarea
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={taskEditForm.title}
                          onChange={(e) => setTaskEditForm({ ...taskEditForm, title: e.target.value })}
                          placeholder="Título de la tarea"
                          className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-amazon-orange outline-none"
                          required
                        />
                        <input
                          type="date"
                          value={taskEditForm.dueDate || ''}
                          onChange={(e) => setTaskEditForm({ ...taskEditForm, dueDate: e.target.value })}
                          className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-amazon-orange outline-none"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSaveTask(task.id)}
                            disabled={submittingTaskEdit}
                            className="bg-amazon-orange text-white px-3 py-1 rounded text-xs disabled:opacity-50 flex items-center gap-1"
                          >
                            {submittingTaskEdit ? (
                              <>
                                <div className="animate-spin rounded-full h-2 w-2 border-b-2 border-white"></div>
                                Guardando...
                              </>
                            ) : (
                              '💾 Guardar'
                            )}
                          </button>
                          <button
                            onClick={cancelEditingTask}
                            disabled={submittingTaskEdit}
                            className="text-gray-500 px-3 py-1 text-xs"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      // Vista normal de la tarea
                      <div className="flex items-center gap-2">
                        <span>☐</span>
                        <span className="text-sm flex-1">{task.title}</span>
                        {task.due_date && (
                          <span className="text-xs text-gray-400">{task.due_date}</span>
                        )}
                        <button
                          onClick={() => startEditingTask(task)}
                          title="Editar tarea"
                          className="p-1.5 sm:p-0 text-gray-400 hover:text-amazon-orange text-sm sm:text-xs"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteTask(task.id)}
                          disabled={deletingTaskId === task.id}
                          title="Eliminar tarea"
                          className="p-1.5 sm:p-0 text-gray-400 hover:text-red-600 text-sm sm:text-xs disabled:opacity-50"
                        >
                          {deletingTaskId === task.id ? '⏳' : '🗑️'}
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Servicios */}
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">🛒 Servicios</h2>
              <button
                onClick={() => setShowServiceForm(!showServiceForm)}
                className="text-amazon-orange hover:underline text-sm"
              >
                {showServiceForm ? 'Cerrar' : '+ Agregar'}
              </button>
            </div>

            {showServiceForm && (
              <form onSubmit={handleAddService} className="mb-4 p-4 bg-gray-50 rounded-lg space-y-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Tipo de servicio</label>
                  <select
                    value={serviceForm.serviceType}
                    onChange={(e) => setServiceForm({ ...serviceForm, serviceType: e.target.value })}
                    className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-amazon-orange outline-none"
                    required
                  >
                    <option value="optimizacion_listing">📦 Optimización Listing</option>
                    <option value="gestion_cuenta_completa">💼 Gestión Cuenta Completa</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Cuota mensual ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={serviceForm.monthlyFee}
                      onChange={(e) => setServiceForm({ ...serviceForm, monthlyFee: e.target.value })}
                      className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-amazon-orange outline-none"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Fecha de inicio</label>
                    <input
                      type="date"
                      value={serviceForm.startDate}
                      onChange={(e) => setServiceForm({ ...serviceForm, startDate: e.target.value })}
                      className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-amazon-orange outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Notas</label>
                  <textarea
                    value={serviceForm.notes}
                    onChange={(e) => setServiceForm({ ...serviceForm, notes: e.target.value })}
                    className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-amazon-orange outline-none"
                    rows={2}
                    placeholder="Notas sobre el servicio..."
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={submittingNewService}
                    className="bg-amazon-orange text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {submittingNewService ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                        Guardando...
                      </>
                    ) : (
                      '💾 Guardar'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowServiceForm(false)}
                    disabled={submittingNewService}
                    className="text-gray-500 px-4 py-2 text-sm"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            )}

            <div className="space-y-3 max-h-80 overflow-y-auto pr-2 -mr-2">
              {contact.services.length === 0 ? (
                <p className="text-gray-500 text-sm">No hay servicios activos</p>
              ) : (
                contact.services.map((service) => (
                  <div key={service.id} className="p-3 bg-gray-50 rounded-lg">
                    {editingServiceId === service.id ? (
                      // Formulario de edición de servicio
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-sm">
                            {service.service_type === 'optimizacion_listing' ? '📦 Optimización Listing' : '💼 Gestión Cuenta Completa'}
                          </span>
                          <span className="text-xs text-gray-400">Editando...</span>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Estado</label>
                            <select
                              value={serviceEditForm.status}
                              onChange={(e) => setServiceEditForm({ ...serviceEditForm, status: e.target.value })}
                              className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-amazon-orange outline-none"
                            >
                              <option value="activo">Activo</option>
                              <option value="pausado">Pausado</option>
                              <option value="finalizado">Finalizado</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Cuota mensual ($)</label>
                            <input
                              type="number"
                              value={serviceEditForm.monthlyFee}
                              onChange={(e) => setServiceEditForm({ ...serviceEditForm, monthlyFee: e.target.value })}
                              className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-amazon-orange outline-none"
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Notas</label>
                          <textarea
                            value={serviceEditForm.notes}
                            onChange={(e) => setServiceEditForm({ ...serviceEditForm, notes: e.target.value })}
                            className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-amazon-orange outline-none"
                            rows={2}
                            placeholder="Notas sobre el servicio..."
                          />
                        </div>
                        
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSaveService(service.id)}
                            disabled={submittingService}
                            className="bg-amazon-orange text-white px-3 py-1 rounded text-xs disabled:opacity-50 flex items-center gap-1"
                          >
                            {submittingService ? (
                              <>
                                <div className="animate-spin rounded-full h-2 w-2 border-b-2 border-white"></div>
                                Guardando...
                              </>
                            ) : (
                              '💾 Guardar'
                            )}
                          </button>
                          <button
                            onClick={cancelEditingService}
                            className="text-gray-500 px-3 py-1 text-xs"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      // Vista normal del servicio
                      <>
                        <div className="flex justify-between items-center">
                          <span className="font-medium">
                            {service.service_type === 'optimizacion_listing' ? '📦 Optimización Listing' : '💼 Gestión Cuenta Completa'}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              service.status === 'activo' ? 'bg-green-100 text-green-800' :
                              service.status === 'pausado' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {service.status}
                            </span>
                            <button
                              onClick={() => startEditingService(service)}
                              title="Editar servicio"
                              className="p-1.5 sm:p-0 text-gray-400 hover:text-amazon-orange text-sm sm:text-xs"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => handleDeleteService(service.id)}
                              disabled={deletingServiceId === service.id}
                              title="Eliminar servicio"
                              className="p-1.5 sm:p-0 text-gray-400 hover:text-red-600 text-sm sm:text-xs disabled:opacity-50"
                            >
                              {deletingServiceId === service.id ? '⏳' : '🗑️'}
                            </button>
                          </div>
                        </div>
                        {service.monthly_fee && (
                          <p className="text-sm text-gray-500 mt-1">
                            ${service.monthly_fee}/mes
                          </p>
                        )}
                        {service.notes && (
                          <p className="text-xs text-gray-400 mt-1">{service.notes}</p>
                        )}
                      </>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
